import React, { useContext, useState, useEffect, Dispatch, SetStateAction } from 'react'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'
import {
  IPDeleteRelatedInstructorsPayload,
  IPDeleteRelatedInstructorsResponse,
  IPGetFutureSessionsPayload,
  IPPutRelatedInstructorsPayload,
  IPPutRelatedInstructorsResponse,
} from '../interface'
import { useMutation } from '@apollo/react-hooks'
import { MutationFunctionOptions } from '@apollo/react-common'
import { DELETE_RELATED_INSTRUCTOR, PUT_RELATED_INSTRUCTORS } from '../mutations'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import PortalPrograms from '@classes/Programs'
import { SCHEDULING_LEVEL, SCHEDULING_LEVEL_LABEL } from '@utils/constants'
import { usePortalFeatures } from '@components/RoleBasedView'
import { GetFutureSessionsResult } from '@interfaces/BlockInstructor'
import { IPBlockFormValues } from '../BlockModal/intreface'
import { THIS_COURSE } from '../BlockModal/constants'

export interface IPBlockedProvider {
  children?: React.ReactNode
  studentId: string
  programId: string
  selectedInstructor?: InstructorSearchResultInfo
  blockedInstructors: InstructorSearchResultInfo[]
  rawInstructors?: InstructorSearchResultInfo[]
}

export interface IBlockFormFields {
  reason: boolean
  blockedFor: boolean
  futureLessonsBookedWillBeCancelled: boolean
  supportCaseRaisedForSessionsWithin24Hours: boolean
}

export type IGetBlockedPayloadType = 'put' | 'delete' | 'get'

export interface IBlockedContext extends IPBlockedProvider {
  allowed: boolean
  isBlocked: (InstructorId?: string) => boolean
  isEditMode: boolean
  setSelectedInstructor: Dispatch<SetStateAction<InstructorSearchResultInfo | undefined>>
  setBlockedInstructors: Dispatch<SetStateAction<InstructorSearchResultInfo[]>>
  setIsEditMode: Dispatch<SetStateAction<boolean>>
  getPayload: (
    type: IGetBlockedPayloadType,
    blockedInstructors: InstructorSearchResultInfo[],
    formValues?: IPBlockFormValues,
    data?: GetFutureSessionsResult
  ) => IPPutRelatedInstructorsPayload | IPDeleteRelatedInstructorsPayload | IPGetFutureSessionsPayload | undefined
  putRelatedInstructors: (options?: MutationFunctionOptions<IPPutRelatedInstructorsResponse> | undefined) => void
  deleteRelatedInstructors: (options?: MutationFunctionOptions<IPDeleteRelatedInstructorsResponse> | undefined) => void
  getSchedulingLevelLabel: (SchedulingLevel: string) => string
  getBlockFormFields: (data?: GetFutureSessionsResult) => IBlockFormFields
  disableBlockButton: (formValues: IPBlockFormValues, data?: GetFutureSessionsResult) => boolean
  unblockInstructors: (instructors: InstructorSearchResultInfo[]) => void
  enableActions?: boolean
}

const BlockedContext = React.createContext<IBlockedContext>({} as IBlockedContext)

export function useBlockedInstructors() {
  return useContext(BlockedContext)
}

export const BlockedProvider: React.FC<IPBlockedProvider> = ({
  studentId: StudentId = '',
  programId = '',
  blockedInstructors: blockedInstructorsProp = [],
  rawInstructors = [],
  children,
}) => {
  const { state } = usePortalFeatures({
    configId: 'flag::FMI-blockedinstructors',
  })
  const Type = 'Blocked'
  const [selectedInstructor, setSelectedInstructor] = useState<InstructorSearchResultInfo | undefined>(undefined)
  const [blockedInstructors, setBlockedInstructors] = useState(blockedInstructorsProp)
  const [isEditMode, setIsEditMode] = useState(false)

  const StudentPrograms = useSelector(
    ({ userProfile }: StoreProps) =>
      new PortalPrograms(userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? [])
  )
  const program = StudentPrograms.getProgram(programId)
  const enableActions = program?.program?.IsGroup ? program?.program?.IsLead : !program?.program?.IsGroup

  useEffect(() => {
    setBlockedInstructors(blockedInstructorsProp)
  }, [blockedInstructorsProp])

  const isBlocked = (InstructorId?: string) =>
    !isEmpty(InstructorId) &&
    blockedInstructors.some(
      (i) => (i.InstructorId === InstructorId && i.IsBlocked) || i.InstructorId === InstructorId
    ) &&
    state.allowed

  const getPayload = (
    type: IGetBlockedPayloadType = 'get',
    blockedInstructors: InstructorSearchResultInfo[] = [],
    formValues?: IPBlockFormValues,
    data?: GetFutureSessionsResult
  ) => {
    if (type === 'delete') {
      return {
        payload: blockedInstructors.map(({ InstructorId = '' }) => ({
          InstructorId,
          StudentId,
          Type,
          ProgramId: programId,
        })),
      }
    }

    if (type === 'put') {
      const hasFutureSessions = data?.getFutureSessions?.[0]?.Sessions || data?.getFutureSessions?.[0]?.Next24Hours
      return {
        putRelatedInstructorsInput: blockedInstructors.map(({ InstructorId = '' }) => ({
          RelatedInstructorInfo: {
            StudentId,
            Type: 'Blocked',
            InstructorId,
            ProgramId: programId,
            BlockedReason: formValues?.reason || '',
            SchedulingLevel:
              formValues?.blockedFor === THIS_COURSE || formValues?.blockedFor === ''
                ? SCHEDULING_LEVEL.PROGRAM_LEVEL
                : SCHEDULING_LEVEL.PROFILE_LEVEL,
            Origin: 'Portal',
            ...(hasFutureSessions ? { ExistingSessionsAction: 'Cancel' } : {}),
          },
        })),
      }
    }

    if (type === 'get') {
      return {
        StudentProfileId: StudentId,
        InstructorId: blockedInstructors?.[0]?.InstructorId!,
        ProgramId: programId,
      }
    }
  }

  const [putRelatedInstructors] = useMutation<IPPutRelatedInstructorsResponse>(PUT_RELATED_INSTRUCTORS, {
    refetchQueries: ['GetInstructorsByProgramId', 'GetSortedInstructorsByProgram'],
  })
  const [deleteRelatedInstructors] = useMutation<IPDeleteRelatedInstructorsResponse>(DELETE_RELATED_INSTRUCTOR, {
    refetchQueries: ['GetInstructorsByProgramId', 'GetSortedInstructorsByProgram'],
  })

  const getSchedulingLevelLabel = (SchedulingLevel: string) =>
    SCHEDULING_LEVEL.PROGRAM_LEVEL === SchedulingLevel
      ? SCHEDULING_LEVEL_LABEL.PROGRAM_LEVEL
      : SCHEDULING_LEVEL_LABEL.PROFILE_LEVEL

  const isPrivateStudent = state.allowed && !program.program?.IsGroup
  const isGroupLeadStudent = state.allowed && !!program.isGroupLead

  const getBlockFormFields = (data?: GetFutureSessionsResult) => {
    const hasFutureSessions = !!data?.getFutureSessions?.[0]?.Sessions
    const hasNext24Hours = !!data?.getFutureSessions?.[0]?.Next24Hours

    // refer to PS-764
    return {
      reason: true,
      blockedFor: isPrivateStudent,
      futureLessonsBookedWillBeCancelled:
        (isPrivateStudent && hasFutureSessions) || (isGroupLeadStudent && hasFutureSessions),
      supportCaseRaisedForSessionsWithin24Hours:
        (isPrivateStudent && hasFutureSessions && hasNext24Hours) ||
        (isGroupLeadStudent && hasFutureSessions && hasNext24Hours),
    }
  }

  const disableBlockButton = (formValues: IPBlockFormValues, data?: GetFutureSessionsResult) => {
    const fields = getBlockFormFields(data)
    return (
      (fields.reason && !formValues.reason) ||
      (fields.blockedFor && !formValues.blockedFor) ||
      (fields.futureLessonsBookedWillBeCancelled && !formValues.futureLessonsBookedWillBeCancelled) ||
      (fields.supportCaseRaisedForSessionsWithin24Hours && !formValues.supportCaseRaisedForSessionsWithin24Hours)
    )
  }

  const unblockInstructors = (instructors: InstructorSearchResultInfo[]) => {
    setBlockedInstructors(
      blockedInstructors.filter((i) => !instructors.some((instructor) => instructor.InstructorId === i.InstructorId))
    )
    deleteRelatedInstructors({
      variables: getPayload('delete', instructors),
    })
  }

  return (
    <BlockedContext.Provider
      value={{
        allowed: state.allowed,
        studentId: StudentId,
        programId,
        selectedInstructor,
        blockedInstructors,
        rawInstructors,
        isBlocked,
        isEditMode,
        setBlockedInstructors,
        setIsEditMode,
        getPayload,
        putRelatedInstructors,
        deleteRelatedInstructors,
        getSchedulingLevelLabel,
        getBlockFormFields,
        disableBlockButton,
        setSelectedInstructor,
        unblockInstructors,
        enableActions,
      }}
    >
      {children}
    </BlockedContext.Provider>
  )
}

export default BlockedProvider
